import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import {
    splitOrderInvoicesDetailTableViewAtom,
    splitOrderInvoicesDetailTableFiltersAtom,
    splitOrderInvoicesDetailTableStatsAtom,
    splitOrderInvoicesDetailTableStateAtom,
    splitOrderInvoicesDetailLineItemsAtom,
    splitOrderInvoicesDetailModelAtom,
    splitOrderInvoicesDetailPageStateAtom,
} from './split_order_invoices_detail_atoms';
import { SplitOrderInvoicesDetailLineItem, SplitOrderInvoicesDetailModel } from './split_order_invoices_detail_types';
import { useTableData } from '../../../core/table/hooks/useTableData';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import {
    splitOrderInvoiceLineItemsAllColumnsView,
    splitOrderInvoiceLineItemsColumnSpec,
    splitOrderInvoiceLineItemsOptionalColumnsView,
} from './SplitOrderInvoiceLineItemsTableSpec';
import { tblSpecLoadView } from '../../../core/table/tableViewsRepo';
import { tableViewVM } from '../../../core/table/table_view_models';

type UseSplitOrderInvoicesDetailDataReturn = {
    items: SplitOrderInvoicesDetailLineItem[];
};

const defaultView = splitOrderInvoiceLineItemsAllColumnsView();
const colSpec = splitOrderInvoiceLineItemsColumnSpec();

export const useSplitOrderInvoiceLineItemsData = (): UseSplitOrderInvoicesDetailDataReturn => {
    const [view] = useImmerAtom(splitOrderInvoicesDetailTableViewAtom);
    const [filters, setFilters] = useImmerAtom(splitOrderInvoicesDetailTableFiltersAtom);
    const [stats, setStats] = useImmerAtom(splitOrderInvoicesDetailTableStatsAtom);
    const [data] = useImmerAtom(splitOrderInvoicesDetailLineItemsAtom);
    const setTableState = useSetImmerAtom(splitOrderInvoicesDetailTableStateAtom);
    const setView = useSetImmerAtom(splitOrderInvoicesDetailTableViewAtom);
    const detailsPageState = useAtomValue(splitOrderInvoicesDetailPageStateAtom);

    const { items } = useTableData<SplitOrderInvoicesDetailLineItem>({ view, filters, stats, data, setStats });

    useEffect(() => {
        if (data.dataItems.length === 0) return;

        const isVeriSaveOnly = detailsPageState.isVeriSaveOnly;
        let hasNdcOptimized = false;
        let showRebates = false;

        data.dataItems.forEach(item => {
            if (!hasNdcOptimized && !!item.NdcOptimized) hasNdcOptimized = true;
            if (!showRebates && !!item.IsRebate) showRebates = true;
        });

        const showOverallocated = !isVeriSaveOnly;
        const showNdcOptimized = hasNdcOptimized && !isVeriSaveOnly;
        const showBacking = !isVeriSaveOnly;
        const needsOptionalView = isVeriSaveOnly || !hasNdcOptimized || !showRebates;

        const activeView = needsOptionalView
            ? splitOrderInvoiceLineItemsOptionalColumnsView(
                  showOverallocated,
                  showNdcOptimized,
                  showRebates,
                  showBacking,
              )
            : defaultView;

        const currentViewId = activeView.key;
        setTableState(draft => {
            draft.views = [defaultView, activeView];
            draft.currentViewId = currentViewId;
        });
        setView(() => tblSpecLoadView(currentViewId, [defaultView, activeView], colSpec) ?? tableViewVM());

        // Apply default IsRebate filter when rebatesOnly is set
        if (detailsPageState.rebatesOnly && showRebates) {
            setFilters(draft => {
                draft['IsRebate'] = { val: 'YES' };
            });
        }
    }, [
        data.dataItems,
        detailsPageState.isVeriSaveOnly,
        detailsPageState.rebatesOnly,
        setTableState,
        setView,
        setFilters,
    ]);

    return { items };
};

export const useSplitOrderInvoicesDetailModel = (): SplitOrderInvoicesDetailModel => {
    return useAtomValue(splitOrderInvoicesDetailModelAtom);
};
